/**
 * Types for the ABO Access tab, "Member app sections" (abo-hide-books-rm).
 * Mirrors, field for field, the digital-services contract of
 * `GET`/`PUT /v1/abo/customers/:id/dashboard-visibility` (TechSpec "Core
 * Interfaces", DS `dto/dashboard-visibility.response.ts`).
 *
 * DS owns the setting and computes the effective value; ABO only shows it and
 * sends the operator's choice. A saved section can never go back to "not set".
 */

/** The values an operator can store for a section. */
export const DASHBOARD_VISIBILITY_VALUES = ["shown", "hidden"] as const;

export type Visibility = (typeof DASHBOARD_VISIBILITY_VALUES)[number];

/** The member app (DMP) sections ABO can show or hide. */
export type DashboardSection = "books" | "magazines";

/** One section as ABO shows it. */
export interface DashboardVisibilitySectionState {
  /** What the member's next token carries (DS's final value). */
  effective: Visibility;
  /** The operator's stored choice; `null` = not set (legacy fallback). */
  stored: Visibility | null;
}

/** The legacy fields the fallback reads, as stored (`null` = absent). */
export interface DashboardVisibilityLegacyFields {
  hideBooks: boolean | null;
  pilotCustomer: boolean | null;
  rmCustomerStatus: string | null;
}

/** Read-only hint from the member's live subscriptions; never applied. */
export type DashboardVisibilitySubscriptionHint =
  | "rm-only"
  | "books-only"
  | "both"
  | "unknown";

/** The latest `dashboard-visibility-changed` audit event of the target. */
export interface DashboardVisibilityLastChange {
  field: DashboardSection;
  from: Visibility | null;
  to: Visibility;
  /** The verified operator e-mail. */
  operator: string;
  /** ISO-8601. */
  at: string;
}

/** PUT only: whether DS wrote anything. */
export type DashboardVisibilityOutcome = "written" | "unchanged";

/** Response of the GET and the PUT. Every value is read from the target. */
export interface DashboardVisibilityResponse {
  /** The `:id` the operator opened. */
  customerId: string;
  /** The preferred case variant of its e-mail: the record the DMP reads. */
  targetCustomerId: string;
  books: DashboardVisibilitySectionState;
  magazines: DashboardVisibilitySectionState;
  legacy: DashboardVisibilityLegacyFields;
  subscriptionHint: DashboardVisibilitySubscriptionHint;
  lastChange: DashboardVisibilityLastChange | null;
  /** PUT only. */
  outcome?: DashboardVisibilityOutcome;
}

/**
 * Body of the PUT: only the sections that changed, at least one. DS refuses
 * unknown keys and an empty body (strict Zod, generic 400).
 */
export interface DashboardVisibilityUpdate {
  books?: Visibility;
  magazines?: Visibility;
}

export const DASHBOARD_VISIBILITY_OPERATOR_UNVERIFIED =
  "ABO_OPERATOR_UNVERIFIED";
export const DASHBOARD_VISIBILITY_OPERATOR_UNAVAILABLE =
  "ABO_OPERATOR_UNAVAILABLE";
export const DASHBOARD_VISIBILITY_CUSTOMER_NOT_FOUND = "CUSTOMER_NOT_FOUND";

/**
 * The typed refusals of the routes (DS `AboRequestRefusal`, ADR-004), each
 * with the HTTP status DS answers it with. A code on any other status is not
 * recognised (same rule as the credits refusal).
 */
export const DASHBOARD_VISIBILITY_REFUSAL_STATUS = {
  [DASHBOARD_VISIBILITY_OPERATOR_UNVERIFIED]: 401,
  [DASHBOARD_VISIBILITY_OPERATOR_UNAVAILABLE]: 503,
  [DASHBOARD_VISIBILITY_CUSTOMER_NOT_FOUND]: 404,
} as const;

export type DashboardVisibilityRefusalCode =
  keyof typeof DASHBOARD_VISIBILITY_REFUSAL_STATUS;

/** Any failure that is not a recognised refusal (500, 400, network error). */
export const DASHBOARD_VISIBILITY_REQUEST_FAILED = "REQUEST_FAILED";

/** A failed GET or PUT, as the Access tab handles it. */
export interface DashboardVisibilityRefusal {
  code:
    | DashboardVisibilityRefusalCode
    | typeof DASHBOARD_VISIBILITY_REQUEST_FAILED;
  /**
   * DS's message for a recognised refusal; `null` otherwise (and when DS
   * sent none), so the tab shows its own copy.
   */
  message: string | null;
  /** HTTP status, or `null` when the request never got an answer. */
  status: number | null;
}
